import React, {useEffect, useMemo, useState} from 'react';
import {Button, SelectInput, Text, TextInput, PhoneInput} from '@/ui-kits';
import {ConsultationReferral, GetSpecialistListForRefferalRequestPayload} from '@/interface';
import {ReferralType} from '@/constants/enums';
import {useSelector, useDispatch} from 'react-redux';

import {appSpecializations, specialistListForRefferal} from '@/services/redux/selectors';
import {getSpecialistListForRefferalRequest} from '@/services/redux/slices';
import {useFormik} from 'formik';
import {alphaNumericWithPeriodRegex, onlyAlphaRegex, onlyAlphaWithParensRegex} from '@/constants';
import {ExternalRefferalValidationSchema} from '@/utils/schema';
import { getSuccessMessage } from '@/utils/getMessages';
import { useToast } from '@/hooks';
import { createFormattedBlurHandler } from '@/utils/common_functions';


interface ReferralSectionProps {
  initialValues: ConsultationReferral | null;
  onSubmit: (value: ConsultationReferral | null) => void;
  className?: string;
  isReadonly?: boolean;
  case_id?: number;
  hospital_specializations?: Array<{id: number; label: string}> | null;
}

const ReferralModal: React.FC<ReferralSectionProps> = props => {
  const {initialValues, onSubmit, className = '', isReadonly = false, case_id, hospital_specializations} = props;
  // ===========================
  // hooks
  // ===========================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {values, handleChange, setFieldValue, handleSubmit, dirty, touched, errors, handleBlur} = useFormik({
    initialValues: initialValues?.data || {
      specialist_id: null,
      is_hospital_specialist: false,
      specialization: null,
      name: '',
      location: '',
      phone: '',
      email: '',
      hospital: '',
      reason: '',
      address: '',
    },
    onSubmit: onSubmitHandler,
    validationSchema: ExternalRefferalValidationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });
  
  // ===========================
  // selectors
  // ===========================
  const allSpecializations = useSelector(appSpecializations) ?? [];
  const availableSpecialists = useSelector(specialistListForRefferal) ?? [];
  
  // ===========================
  // states
  // ===========================
  // Use hospital specializations if available, otherwise use all specializations
  const [referralType, setReferralType] = useState<ReferralType | null>(null);
  const labelClassName = 'text-ui_blue !font-FigtreeSemiBold';
  const inputClassName = 'px-4 py-2.5 font-FigtreeRegular text-sm text-[#121212]';


  // ===========================
  // memoized values
  // ===========================
  const specializations = useMemo(() => {
    return allSpecializations;
  }, [allSpecializations]);

  const internalSpecialization = useMemo<string>(() => {
    if (typeof values.specialization === 'number') {
      return '';
    }
    return values.specialization ?? '';
  }, [values.specialization]);

  const specialistsWithHospitalInfo = useMemo(() => {
    return availableSpecialists.map(specialist => ({
      ...specialist,
      label: specialist.is_hospital_specialist && specialist.hospital_name
        ? `${specialist.label} (${specialist.hospital_name}${specialist.hospital_address ? ', ' + specialist.hospital_address : ''})`
        : specialist.label,
    }));
  }, [availableSpecialists]);

  const buttonDisabled = useMemo(() => {
    if (!dirty) return true;
    if (referralType === ReferralType.Internal) {
      return !(values.specialization && values.specialist_id && typeof values.specialization === 'number' && values.reason);
    }

    return !(values.name && values.specialization && typeof values.specialization === 'string' && values.location && values.reason) || !!errors.email || !!errors.phone;
  }, [referralType, values.specialization, values.specialist_id, values.name, values.location, values.reason, errors.email, errors.phone]);

  //==============================================//
  // functions
  //==============================================//
  function onSubmitHandler(values: ConsultationReferral['data']) {
    if (!referralType) return;

    let payload: ConsultationReferral['data'] = {};
    if (referralType === ReferralType.Internal) {
      payload = {
        specialist_id: values.specialist_id,
        is_hospital_specialist: values.is_hospital_specialist || false,
        specialization: values.specialization,
        hospital: values.hospital || '',
      };
    } else {
      payload = {
        name: values.name,
        specialization: values.specialization,
        location: values.location,
        phone: values.phone,
        email: values.email,
        hospital: values.hospital,
        address: values.address,
      };
    }
    payload.reason = values.reason;
    onSubmit({
      type: referralType,
      data: payload,
    });

    showToast(getSuccessMessage('S-10119'), 'success');
  }

  function handleSelectReferralType(type: ReferralType) {
    setReferralType(type);
    // reset the reason 
    setFieldValue('reason', '')
  }

  function handleSelectSpecialist(specialist: any) {
    setFieldValue('specialist_id', specialist.id);
    setFieldValue('is_hospital_specialist', specialist.is_hospital_specialist || false);
    setFieldValue('hospital', specialist.hospital_name || '');
  }

  //==============================================//
  // side effects
  //==============================================//
  useEffect(() => {
    if (values.specialization && typeof values.specialization === 'number') {
      const payload: GetSpecialistListForRefferalRequestPayload = {
        specialization: values.specialization,
        ...(case_id && {case_id}),
      };
      dispatch(getSpecialistListForRefferalRequest(payload));
    }
  }, [values.specialization, case_id, dispatch]);

  useEffect(() => {
    if (initialValues?.type) {
      setReferralType(initialValues.type);
    } else {
      setReferralType(null);
    }
  }, [initialValues?.type]);


  const reasonTexrInput = (
    <TextInput
      label="Reason for referral"
      value={values.reason || ''}
      placeholder="Add Comments"
      onChangeText={handleChange('reason')}
      disabled={isReadonly}
      wrapperClassName="w-full"
      className="w-full"
      multiline
      labelClassName={labelClassName}
      inputClassName={inputClassName}
      preventLeadingSpace
      preventTrailingSpace
      allowedRegex={alphaNumericWithPeriodRegex}
    />
  );

  return (
    <section className={`rounded-xl border border-ui_input_border/40 bg-white px-4 sm:px-5 md:px-6 py-4 sm:py-6 ${className}`}>
      <Text variant="title" className="text-ui_blue mb-1 !text-base sm:!text-lg">
        Referral (Optional)
      </Text>
      <Text className="font-FigtreeRegular !text-xs sm:!text-sm text-ui_input_border_dark mt-1">
        Refer this case to another specialist if additional expertise is needed.
      </Text>

      <div className="mt-5 space-y-4">
        <label
          aria-label="Internal Referral"
          className={`flex cursor-pointer items-center gap-3 py-2 transition-opacity ${isReadonly ? 'opacity-60 cursor-not-allowed' : ''}`}>
          <input
            type="radio"
            name="referral_choice"
            value={ReferralType.Internal}
            disabled={isReadonly}
            checked={referralType === ReferralType.Internal}
            onChange={() => handleSelectReferralType(ReferralType.Internal)}
            className="size-5 !border-ui_blue text-ui_blue focus:ring-ui_blue accent-ui_blue"
          />
          <div>
            <Text variant="free" className="font-FigtreeSemiBold text-lg text-ui_input_border_dark">
              Internal Referral
            </Text>
          </div>
        </label>

        {referralType === ReferralType.Internal && (
          <div className="rounded-xl border ml-0 sm:ml-6 md:ml-8 mt-4 border-ui_input_border/50 bg-white p-6 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-start md:gap-6">
              <SelectInput
                label="Specialization"
                placeholder="Select Specialization"
                value={values.specialization as number | null}
                options={specializations}
                onChange={item => setFieldValue('specialization', item.id)}
                disabled={isReadonly}
                className="min-w-[200px]"
                labelClassName={labelClassName}
              />

              <SelectInput
                label="Select Specialist"
                placeholder="Choose a specialist"
                value={values.specialist_id as number | null}
                options={specialistsWithHospitalInfo}
                onChange={item => handleSelectSpecialist(item)}
                disabled={isReadonly || !values.specialization || availableSpecialists.length === 0}
                className='min-w-[200px]'
                labelClassName={labelClassName}
                error={availableSpecialists.length === 0 ? 'No specialists for the given specialization' : ''}
              />
            </div>
            {reasonTexrInput}

            {!isReadonly && <Button className="self-center" title="Confirm Referral" onClick={() => handleSubmit()} disabled={buttonDisabled} />}
          </div>
        )}

        <label
          aria-label="External Referral"
          className={`flex cursor-pointer items-center gap-3 py-2 transition-opacity ${isReadonly ? 'opacity-60 cursor-not-allowed' : ''}`}>
          <input
            type="radio"
            name="referral_choice"
            value={ReferralType.External}
            disabled={isReadonly}
            checked={referralType === ReferralType.External}
            onChange={() => handleSelectReferralType(ReferralType.External)}
            className="size-5 border-ui_input_border_dark text-ui_blue focus:ring-ui_blue accent-ui_blue"
          />
          <div>
            <Text variant="free" className="font-FigtreeSemiBold text-lg text-ui_input_border_dark">
              External Referral
            </Text>
          </div>
        </label>

        {referralType === ReferralType.External && (
          <div className="rounded-xl ml-0 sm:ml-6 md:ml-8 mt-4 border border-ui_input_border/50 bg-white p-6 shadow-sm space-y-4 flex flex-col">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextInput
                label="Full Name"
                allowedRegex={onlyAlphaRegex}
                preventLeadingSpace
                preventTrailingSpace
                value={values.name ?? ''}
                placeholder="Dr. Full Name"
                onChangeText={handleChange('name')}
                disabled={isReadonly}
                wrapperClassName="w-full"
                className="w-full"
                onBlur={createFormattedBlurHandler(handleBlur, setFieldValue, 'name')}
                labelClassName={labelClassName}
                inputClassName={inputClassName}
              />

              <TextInput
                label="Specialization"
                allowedRegex={onlyAlphaWithParensRegex}
                preventLeadingSpace
                preventTrailingSpace
                value={internalSpecialization}
                onBlur={createFormattedBlurHandler(handleBlur, setFieldValue, 'specialization')}
                placeholder="e.g. Cardiology"
                onChangeText={handleChange('specialization')}
                disabled={isReadonly}
                wrapperClassName="w-full"
                className="w-full"
                labelClassName={labelClassName}
                inputClassName={inputClassName}
              />

              <TextInput
                label="Location"
                allowedRegex={onlyAlphaWithParensRegex}
                preventLeadingSpace
                preventTrailingSpace
                value={values.location || ''}
                onBlur={createFormattedBlurHandler(handleBlur, setFieldValue, 'location')}
                placeholder="City, State"
                onChangeText={handleChange('location')}
                disabled={isReadonly}
                wrapperClassName="w-full"
                className="w-full"
                labelClassName={labelClassName}
                inputClassName={inputClassName}
              />

              <PhoneInput
                label="Contact Phone no."
                showOptionalLabel
                value={values?.phone || ''}
                placeholder="Enter Phone Number"
                onChange={phone => {setFieldValue('phone', phone, true)}}
                disabled={isReadonly}
                wrapperClassName="w-full"
                className="w-full"
                labelClassName={labelClassName}
                inputClassName={inputClassName}
                error={errors.phone}
                touched={touched.phone}
                onBlur={handleBlur('phone')}
              />
              <TextInput
                label="Hospital name"
                preventLeadingSpace
                preventTrailingSpace
                showOptionalLabel
                value={values?.hospital || ''}
                onBlur={createFormattedBlurHandler(handleBlur, setFieldValue, 'hospital')}
                placeholder="eg. Aiswarya hospital"
                onChangeText={handleChange('hospital')}
                disabled={isReadonly}
                wrapperClassName="w-full"
                className="w-full"
                labelClassName={labelClassName}
                inputClassName={inputClassName}
              />
              <TextInput
                label="Contact Email"
                touched={touched.email}
                error={errors.email}
                onBlur={handleBlur('email')}
                showOptionalLabel
                value={values?.email || ''}
                placeholder="Enter Email"
                onChangeText={handleChange('email')}
                disabled={isReadonly}
                wrapperClassName="w-full"
                className="w-full"
                labelClassName={labelClassName}
                inputClassName={inputClassName}
              />
            </div>

            <TextInput
              label="Address"
              preventLeadingSpace
              preventTrailingSpace
              showOptionalLabel
              value={values.address || ''}
              placeholder="Full Address"
              onChangeText={handleChange('address')}
              onBlur={createFormattedBlurHandler(handleBlur, setFieldValue, 'address')}
              disabled={isReadonly}
              wrapperClassName="w-full"
              className="w-full"
              labelClassName={labelClassName}
              inputClassName={inputClassName}
            />

            {reasonTexrInput}

            {!isReadonly && <Button className="self-center" title="Confirm Referral" onClick={() => handleSubmit()} disabled={buttonDisabled} />}
          </div>
        )}
      </div>
    </section>
  );
};

export default ReferralModal;
